import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorFromResponse, authFetch } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { ApiError, type ProblemDetail } from '@/lib/errors'
import type { components } from '@/../types/api'

export type UploadKickoffIn = components['schemas']['UploadKickoffIn']
export type MarkUploadIn = components['schemas']['MarkUploadIn']
export type UploadStatusOut = components['schemas']['UploadStatusOut']
export type YoutubeVideoOut = components['schemas']['YoutubeVideoOut']
export type ChannelStatsOut = components['schemas']['ChannelStatsOut']
export type UploadJobOut = components['schemas']['UploadJobOut']
export type StatsSyncOut = components['schemas']['StatsSyncOut']

/**
 * Per-channel upload tracker rows + OAuth status. Polled slowly (15s) so
 * the ProjectDetailPage and upload modal can reflect auth state and
 * already-uploaded videos without requiring a manual refresh.
 */
export function useUploadStatus(channel: string) {
  return useQuery({
    queryKey: queryKeys.uploads.status(channel),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/uploads/status', {
        params: { query: { channel } },
      })
      if (!data) throw new Error('upload status empty')
      return data.data
    },
    enabled: Boolean(channel),
    refetchInterval: 15_000,
    staleTime: 5_000,
  })
}

/**
 * Recent videos on the connected YouTube channel (read-through to the
 * Data API via the backend). Not polled — the UI refetches on demand.
 */
export function useChannelYoutubeVideos(channel: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.uploads.youtubeVideos(channel),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/uploads/youtube-videos', {
        params: { query: { channel } },
      })
      if (!data) throw new Error('youtube videos empty')
      return data.data
    },
    enabled: Boolean(channel) && enabled,
    staleTime: 60_000,
  })
}

export function useChannelStats(channel: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.uploads.channelStats(channel),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/uploads/channel-stats', {
        params: { query: { channel } },
      })
      if (!data) throw new Error('channel stats empty')
      return data.data
    },
    enabled: Boolean(channel) && enabled,
    staleTime: 60_000,
  })
}

/**
 * Per-video YouTube counters (views/likes/comments) for every published
 * video in the channel. Returned as a map keyed by ``youtube_video_id``.
 * Hits the YouTube Data API on the backend, so it's slower than the local
 * tracker — cached for 60s.
 */
export function useChannelVideoStats(channel: string, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.uploads.all, 'video-stats', channel],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/uploads/video-stats', {
        params: { query: { channel } },
      })
      if (!data) throw new Error('video stats empty')
      return data.data
    },
    enabled: Boolean(channel) && enabled,
    staleTime: 60_000,
  })
}

/**
 * When Prism last synced this channel's YouTube counters — the hourly cron
 * or the "Atualizar agora" button, whichever ran last. A plain DB read on
 * the backend (no quota), so it polls every minute to catch cron ticks
 * while the page stays open.
 */
export function useStatsSync(channel: string) {
  return useQuery({
    queryKey: queryKeys.uploads.statsSync(channel),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/uploads/stats-sync', {
        params: { query: { channel } },
      })
      if (!data) throw new Error('stats sync empty')
      return data.data
    },
    enabled: Boolean(channel),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

/**
 * Fires the upload job. Returns a ``job_key`` the caller polls via the
 * shared jobs feed (so the toast infra picks up success/failure). Invalidates
 * the upload status list so the ProjectDetailPage shows the new row
 * immediately.
 */
export function useStartUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: UploadKickoffIn): Promise<UploadJobOut> => {
      const { data } = await api.POST('/api/v1/uploads', { body: input })
      if (!data) throw new Error('upload kickoff empty')
      return data.data
    },
    onSuccess: (_data, vars) => {
      const channel = vars.channel ?? ''
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      if (channel) {
        void qc.invalidateQueries({ queryKey: queryKeys.uploads.status(channel) })
      }
    },
  })
}

/**
 * Disconnect the YouTube OAuth tokens for a channel. Hits the legacy
 * ``/auth/youtube/disconnect`` endpoint (outside ``/api/v1``) which lives
 * on the render router because the OAuth round-trip itself does. After
 * success the upload status query is invalidated so the UI flips to
 * "not authenticated" without a manual refresh.
 */
export function useDisconnectYoutube() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (channel: string) => {
      const res = await authFetch(`/auth/youtube/disconnect?channel=${encodeURIComponent(channel)}`, {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao desconectar')
      }
      return (await res.json()) as { ok: boolean }
    },
    onSuccess: (_data, channel) => {
      void qc.invalidateQueries({ queryKey: queryKeys.uploads.status(channel) })
    },
  })
}

export interface RefreshStatsResult {
  channel_slug: string
  videos_synced: number
}

interface Envelope<T> {
  data: T
}

/**
 * Manually trigger the YouTube stats sync for a channel. Same work the
 * 60-min cron does (reconcile orphan uploads → upsert views/likes/comments
 * into ``youtube_video_stats``). Useful right after publishing — otherwise
 * stats refresh on the next cron tick.
 *
 * Server-side cooldown is 60s/channel; if violated, the response is HTTP
 * 429 and ``mutation.error`` carries the wait-time message. On success we
 * invalidate the channel-stats / video-stats / peak-hours queries so the
 * UI repaints with fresh numbers.
 */
export function useRefreshUploadStats() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (channel: string): Promise<RefreshStatsResult> => {
      const params = new URLSearchParams({ channel })
      const res = await authFetch(`/api/v1/uploads/refresh-stats?${params.toString()}`, {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (!res.ok) {
        let body: unknown = null
        try {
          body = await res.clone().json()
        } catch {
          /* ignore */
        }
        const problem = isProblemDetail(body) ? body : null
        const message = problem?.detail ?? problem?.title ?? ''
        throw new ApiError(message || 'Falha ao atualizar as estatísticas', res.status, problem, body)
      }
      const json = (await res.json()) as Envelope<RefreshStatsResult>
      return json.data
    },
    onSuccess: (_data, channel) => {
      void qc.invalidateQueries({ queryKey: queryKeys.uploads.channelStats(channel) })
      void qc.invalidateQueries({ queryKey: [...queryKeys.uploads.all, 'video-stats', channel] })
      void qc.invalidateQueries({ queryKey: queryKeys.uploads.status(channel) })
      void qc.invalidateQueries({ queryKey: queryKeys.uploads.statsSync(channel) })
      // Peak hours card uses /analytics/peak-hours which derives from the
      // same Data API call; refreshing the cron data doesn't invalidate
      // its source, but invalidating here keeps the user mental model
      // consistent ("apertei atualizar e tudo recarregou").
      void qc.invalidateQueries({ queryKey: queryKeys.analytics.all })
    },
  })
}

function isProblemDetail(body: unknown): body is ProblemDetail {
  if (!body || typeof body !== 'object') return false
  const obj = body as Record<string, unknown>
  return typeof obj['title'] === 'string' && typeof obj['status'] === 'number'
}

/**
 * Manual status override (marks a video as published externally, or clears
 * a bad record). Separate from the real upload flow because sometimes the
 * user uploads from the YouTube website directly and the tracker needs to
 * catch up.
 */
export function useMarkUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: MarkUploadIn) => {
      const { data } = await api.POST('/api/v1/uploads/mark', { body: input })
      if (!data) throw new Error('mark upload empty')
      return data.data
    },
    onSuccess: (_data, vars) => {
      const channel = vars.channel ?? ''
      if (channel) {
        void qc.invalidateQueries({ queryKey: queryKeys.uploads.status(channel) })
      }
    },
  })
}
